<script setup lang="ts">
import { HttpStatusCode } from "axios";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type LxcError from "~/core/models/LxcError.class";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
} from "~/modules/businessUnit/constants";
import {
  PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_DELEGATIONS,
} from "~/modules/organization/constants";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useFetchAffiliationChildren } from "~/modules/relation/affiliation/composables/useFetchAffiliationChildren";
import { useGetAffiliationParent } from "~/modules/relation/affiliation/composables/useGetAffiliationParent";
import { useFetchGivenDelegations } from "~/modules/relation/delegation/composables/useFetchGivenDelegations";
import { useFetchReceivedDelegations } from "~/modules/relation/delegation/composables/useFetchReceivedDelegations";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";

const props = defineProps<
  GenericParameters & {
    organizationType?: OrganizationType;
  }
>();

const router = useRouter();

const { can } = useAcl();

const isLoadingAffiliation = ref(true);
const isLoadingDelegation = ref(true);

const parentAffiliation: Ref<RelationEntity | undefined> = ref();
const errorParentAffiliation: Ref<LxcError | undefined> = ref();

// shallowRef is used here in order to not unwrap internal refs and respect the interfaces
const fetchAffiliationChildrenResponse =
  shallowRef<UseFetchResponse<RelationEntity>>();
const fetchGivenDelegationResponse =
  shallowRef<UseFetchResponse<RelationEntity>>();
const fetchReceivedDelegationResponse =
  shallowRef<UseFetchResponse<RelationEntity>>();

/**
 * Tabs configuration
 */
const affiliationsTabId = "affiliations";
const delegationsTabId = "delegations";

const affliationsRedirectPath = router.resolve({
  name:
    props.organizationCode !== undefined
      ? PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_AFFILIATIONS
      : PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_AFFILIATIONS,
  params: { id: props.organizationCode ?? props.businessUnitCode },
}).href;

const delegationRedirectPath = router.resolve({
  name:
    props.organizationCode !== undefined
      ? PATH_ORGANIZATIONS_ID_SETTINGS_RELATIONS_DELEGATIONS
      : PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS_DELEGATIONS,
  params: { id: props.organizationCode ?? props.businessUnitCode },
}).href;

// tab navigations configuration
const tabNavigations = computed(() => {
  const tabNavigations = [];

  if (displayTabAffiliation.value) {
    tabNavigations.push({
      id: affiliationsTabId,
      redirectPath: affliationsRedirectPath,
      labelTranslationKey: "administration.relations.affiliations.title",
    });
  }

  if (displayTabDelegation.value) {
    tabNavigations.push({
      id: delegationsTabId,
      redirectPath: delegationRedirectPath,
      labelTranslationKey: "administration.relations.delegations.title",
    });
  }

  return tabNavigations;
});

// Only display the tab affiliation if (OR):
//   - a parent affiliation exists
//   - BU or organization of type administration with (OR):
//     - permission CAN_WRITE_RELATION
//     - permission CAN_READ_RELATION and at least one affiliation
const displayTabAffiliation = computed(
  () =>
    parentAffiliation.value ||
    (errorParentAffiliation.value &&
      errorParentAffiliation.value?.status !== HttpStatusCode.NotFound) ||
    ((props.organizationCode === undefined ||
      props.organizationType === OrganizationType.ADMINISTRATION) &&
      (can(ACLRoles.CAN_WRITE_RELATION) ||
        (can(ACLRoles.CAN_READ_RELATION) &&
          (!!fetchAffiliationChildrenResponse.value?.results.value?.results
            .length ||
            !!fetchAffiliationChildrenResponse.value?.error.value)))),
);

// Only display the tab delegation if (OR):
//   - organization of type operator with (OR):
//     - permission CAN_WRITE_RELATION
//     - permission CAN_READ_RELATION and at least one given delegation
//   - BU or organization of type administration with at least one received delegation
const displayTabDelegation = computed(
  () =>
    (props.organizationType === OrganizationType.OPERATOR &&
      (can(ACLRoles.CAN_WRITE_RELATION) ||
        (can(ACLRoles.CAN_READ_RELATION) &&
          (!!fetchGivenDelegationResponse.value?.results.value?.results
            .length ||
            !!fetchGivenDelegationResponse.value?.error)))) ||
    ((props.organizationCode === undefined ||
      props.organizationType === OrganizationType.ADMINISTRATION) &&
      (!!fetchReceivedDelegationResponse.value?.results.value?.results.length ||
        !!fetchReceivedDelegationResponse.value?.error.value)),
);
/**
 * End Tabs configuration
 */

/**
 * Fetch affiliation children
 */
async function fetchAffiliationChildren() {
  fetchAffiliationChildrenResponse.value = useFetchAffiliationChildren(props);

  fetchAffiliationChildrenResponse.value?.onSearch(
    fetchAffiliationChildrenResponse.value?.fetchData,
  );
}

/**
 * Fetch delegation given
 */
async function fetchGivenDelegations() {
  fetchGivenDelegationResponse.value = useFetchGivenDelegations(props);

  fetchGivenDelegationResponse.value.onSearch(
    fetchGivenDelegationResponse.value.fetchData,
  );
}

/**
 * Fetch delegation received
 */
async function fetchReceivedDelegations() {
  fetchReceivedDelegationResponse.value = useFetchReceivedDelegations(props);

  fetchReceivedDelegationResponse.value.onSearch(
    fetchReceivedDelegationResponse.value.fetchData,
  );
}

onMounted(async () => {
  // Case when relationships on an organization
  if (props.organizationCode) {
    // Get the parent affiliation
    const { getAffiliationParent, error, result } = useGetAffiliationParent({
      organizationCode: props.organizationCode,
    });
    await getAffiliationParent();

    if (error.value) {
      errorParentAffiliation.value = error.value;
    } else {
      parentAffiliation.value = result.value;
    }

    if (props.organizationType === OrganizationType.ADMINISTRATION) {
      // Get the children affiliations
      await fetchAffiliationChildren();

      // Get the received delegations
      await fetchReceivedDelegations();
    } else if (props.organizationType === OrganizationType.OPERATOR) {
      // Get the given delegations
      await fetchGivenDelegations();
    }
  }
  // Case when relationships on a BU
  else {
    // Get the children affiliations
    await fetchAffiliationChildren();

    // Get the received delegations
    await fetchReceivedDelegations();
  }

  isLoadingAffiliation.value = false;
  isLoadingDelegation.value = false;

  // Update path
  if (displayTabAffiliation.value) {
    if (router.currentRoute.value.path !== affliationsRedirectPath) {
      router.replace(affliationsRedirectPath);
    }
  } else {
    if (router.currentRoute.value.path !== delegationRedirectPath) {
      router.replace(delegationRedirectPath);
    }
  }
});
</script>

<template>
  <div class="flex h-full">
    <div class="w-3/5">
      <container-component
        :is-loading="
          isLoadingAffiliation ||
          isLoadingDelegation ||
          fetchAffiliationChildrenResponse?.isLoading.value ||
          fetchGivenDelegationResponse?.isLoading.value ||
          fetchReceivedDelegationResponse?.isLoading.value
        "
        :px="0"
        :py="0"
      >
        <tabs-with-navigation
          :tab-navigations="tabNavigations"
          tab-position="left"
        >
          <template #[affiliationsTabId]>
            <affiliation-component
              v-bind="props"
              :parent-affiliation="parentAffiliation"
              :error-parent-affiliation="errorParentAffiliation"
              :fetch-affiliation-children-response="
                fetchAffiliationChildrenResponse
              "
            />
          </template>

          <template #[delegationsTabId]
            ><delegation-component
              v-bind="props"
              :fetch-given-delegation-response="fetchGivenDelegationResponse"
              :fetch-received-delegation-response="
                fetchReceivedDelegationResponse
              "
            />
          </template>
        </tabs-with-navigation>
      </container-component>
    </div>

    <div class="flex-1 -mt-5 border-x border-gray-200 px-7 py-5">
      <relation-referent-user v-bind="props" />
    </div>
  </div>
</template>
